import React from 'react'
import { PromptEditorContextState } from './context'
import { UpdatePromptGroupRequest } from '@pushly/models/lib/structs/prompts/update-prompt-group-request'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { DomainDto } from '../../../dtos/domain'
import { GetPromptGroupResponse } from '@pushly/models/lib/structs/prompts/get-prompt-group-response'
import { PromptEditorGroup, PromptEditorTreatment } from './types'
import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import type { PropertiesOf } from '@pushly/models/lib/types'

export type PromptEditorDispatchPack =
    | { type: 'set-domain'; domain: DomainDto }
    | { type: 'set-prompt-group-source'; group: GetPromptGroupResponse }
    | { type: 'set-prompt-group'; group: PromptEditorGroup }
    | { type: 'set-prompts'; prompts: PromptEditorTreatment[] }
    | { type: 'add-prompt'; prompt: PromptEditorTreatment }
    | { type: 'set-prompt'; idx: number; prompt: PromptEditorTreatment }
    | { type: 'rem-prompt-idx'; idx: number }
    | { type: 'set-selected-treatment-idx'; idx: number }
    | { type: 'set-selected-delivery-channel'; channel: DeliveryChannel }
    | { type: 'set-is-ab-test-enabled'; enabled: boolean; skipPromptsReset?: boolean }
    | { type: 'set-is-auto-show-enabled'; enabled: boolean }

function isLoading(state: PromptEditorContextState): boolean {
    return !state.domain || !state.promptGroup || !state.prompts?.length
}

export function promptEditorReducer(
    state: PromptEditorContextState,
    action: PromptEditorDispatchPack,
): PromptEditorContextState {
    const nextState = { ...state }

    switch (action.type) {
        case 'set-domain': {
            nextState.domain = action.domain
            nextState.activeDeliveryChannels = getEnabledDeliveryChannels(action.domain)
            nextState.loading = isLoading(nextState)
            break
        }
        case 'set-prompt-group-source': {
            nextState.promptGroupSource = action.group
            nextState.promptGroup = UpdatePromptGroupRequest.from(action.group)
            nextState.loading = isLoading(nextState)
            break
        }
        case 'set-prompt-group': {
            nextState.promptGroup = action.group
            nextState.loading = isLoading(nextState)
            break
        }
        case 'set-prompts': {
            nextState.prompts = action.prompts
            nextState.selectedPromptIdx = 0
            nextState.selectedPrompt = action.prompts[0]
            nextState.loading = isLoading(nextState)
            break
        }
        case 'add-prompt': {
            if (state.loading) break

            nextState.selectedPromptIdx = state.prompts?.length
            nextState.prompts = [...state.prompts, action.prompt]
            nextState.selectedPrompt = action.prompt
            break
        }
        case 'set-prompt': {
            if (state.loading) break

            const nextPrompts = [...state.prompts]

            // set prompt
            nextPrompts.splice(action.idx, 1, action.prompt)

            nextState.selectedPrompt = action.prompt
            nextState.prompts = nextPrompts
            break
        }
        case 'rem-prompt-idx': {
            if (state.loading) break

            const nextPrompts = [...state.prompts]

            // rem prompt
            nextPrompts.splice(action.idx, 1)

            // get next index
            const nextSelectedIdx =
                action.idx > state.selectedPromptIdx
                    ? state.selectedPromptIdx
                    : state.selectedPromptIdx - 1 <= 0
                    ? 0
                    : state.selectedPromptIdx - 1

            nextState.selectedPromptIdx = nextSelectedIdx
            nextState.selectedPrompt = nextPrompts[nextSelectedIdx]
            nextState.prompts = nextPrompts
            break
        }
        case 'set-selected-treatment-idx': {
            nextState.selectedPromptIdx = action.idx
            nextState.selectedPrompt = state.prompts?.[action.idx]
            break
        }
        case 'set-selected-delivery-channel': {
            nextState.selectedDeliveryChannel = action.channel
            break
        }
        case 'set-is-ab-test-enabled': {
            if (state.loading) break

            nextState.isAbTestEnabled = action.enabled
            nextState.promptGroup!.isAbTest = action.enabled

            if (!action.skipPromptsReset) {
                const currPrompt = nextState.selectedPrompt!.clone()
                // When A/B enabled set prompt weight to 100; else set to 50
                currPrompt.weight = !action.enabled ? 100 : 50

                // ensure a single prompt only on toggle
                nextState.prompts = [currPrompt]
                nextState.selectedPrompt = currPrompt
                nextState.selectedPromptIdx = 0
            }

            break
        }
        case 'set-is-auto-show-enabled': {
            nextState.isAutoShowEnabled = action.enabled
            nextState.prompts = state.prompts?.map((p) =>
                p.clone({
                    isAutoShow: action.enabled,
                }),
            )
            break
        }
    }

    return nextState
}

export const PromptEditorDispatchContext = React.createContext<React.Dispatch<PromptEditorDispatchPack>>(null!)

/* webpack-strip-block:removed */
